import React from "react";
import { NavLink } from "react-router-dom";
import {
  Invit,
  YearDataOperationalMetrics,
  YearDataOperationalMetricsIndus,
  YearDataOperationalMetricsPG,
} from "../../../../types/invit.types";
import {
  findLatestValidValue,
  toWholeNumber,
} from "../../../../utils/financialHelper";
import "./InvitBanner.less";

interface BannerProps {
  invit: Invit | null;
}

const compareArrow = new URL(
  "../../../../assets/compare-arrow.png",
  import.meta.url
).href;

const InvitBanner: React.FC<BannerProps> = ({ invit }) => {
  const displayName = "cmp-invit-banner";
  if (!invit) return null;

  const industry = (() => {
    if (!invit?.operationalMetrics?.length) return "-";

    const firstMetric = invit.operationalMetrics[0] as
      | YearDataOperationalMetrics
      | YearDataOperationalMetricsIndus
      | YearDataOperationalMetricsPG;

    const firstSemi =
      "semiAnnuals" in firstMetric && firstMetric.semiAnnuals?.length
        ? firstMetric.semiAnnuals[0]
        : null;

    let sectorName = (firstSemi as any)?.sectorOfOperation?.name ?? "-";

    const industryOverrides: Record<string, string> = {
      "Maple Infrastructure Trust": "Roads",
    };

    if (industryOverrides[invit.name]) {
      sectorName = industryOverrides[invit.name];
    }

    if (sectorName === "Roads") return "Road Infrastructure";
    return sectorName || "-";
  })();

  const listingDates: Record<string, string> = {
    "IRB InvIT Fund": "18 May 2017",
    "Indus Infra Trust": "12 March 2024",
    "PowerGrid InvIT": "14 May 2021",
    "IndiGrid InvIT": "6 June 2017",
    "Capital Infra Trust": "17 January 2025",
  };
  const listingDate = listingDates[invit.name] || "-";

  const isPrivate = invit?.isPublic === false;

  return (
    <section className={displayName}>
      <div className={`${displayName}__overlay`}></div>

      <div className={`${displayName}__content`}>
        <div className={`${displayName}__right`}>
          {/* Top Section */}
          <div className={`${displayName}__top`}>
            <div className={`${displayName}__top-left`}>
              <h2>{invit.name}</h2>
            </div>
            <div className={`${displayName}__top-right`}>
              <p style={{ marginBottom: "5px" }}>
                Industry:<strong> {industry}</strong>
              </p>
              {!isPrivate && (
                <p>
                  Listing Date:<strong> {listingDate}</strong>
                </p>
              )}
            </div>
          </div>

          {/* Bottom Section */}
          <div className={`${displayName}__bottom`}>
            <div className={`${displayName}__bottom-left`}>
              <div className="reit-stats-row">
                {(() => {
                  const latestMarketCap = findLatestValidValue(
                    invit.financialMetrics,
                    "marketCapitalisation"
                  );
                  const formattedMarketCap = latestMarketCap
                    ? toWholeNumber(latestMarketCap)
                    : "-";

                  const nseData = !isPrivate
                    ? invit.historicalDataSets?.find(
                        (d: any) =>
                          d.marketIndexPrice?.exchangeName?.toLowerCase() ===
                          "nse"
                      )
                    : null;

                  const bseData = !isPrivate
                    ? invit.historicalDataSets?.find(
                        (d: any) =>
                          d.marketIndexPrice?.exchangeName?.toLowerCase() ===
                          "bse"
                      )
                    : null;

                  const nsePrice =
                    nseData?.marketIndexPrice?.historicalData?.[0]
                      ?.closingPrice ?? 0;
                  const bsePrice =
                    bseData?.marketIndexPrice?.historicalData?.[0]
                      ?.closingPrice ?? 0;

                  const nseChange =
                    nseData?.marketIndexPrice?.historicalData?.[0]
                      ?.changeInPrice ?? 0;
                  const bseChange =
                    bseData?.marketIndexPrice?.historicalData?.[0]
                      ?.changeInPrice ?? 0;

                  const getChangeColor = (val: number) =>
                    val > 0 ? "green" : val < 0 ? "red" : "gray";

                  return (
                    <>
                      {!isPrivate && (
                        <>
                          <div className="stat-block">
                            <span>
                              BSE <span className="tag">Prev Close</span>
                            </span>
                            <p>
                              {bsePrice.toFixed(2)}{" "}
                              <span
                                className="change"
                                style={{ color: getChangeColor(bseChange) }}
                              >
                                ({bseChange > 0 ? "+" : ""}
                                {bseChange.toFixed(2)})
                              </span>
                            </p>
                          </div>

                          <div className="stat-block">
                            <span>
                              NSE <span className="tag">Prev Close</span>
                            </span>
                            <p>
                              {nsePrice.toFixed(2)}{" "}
                              <span
                                className="change"
                                style={{ color: getChangeColor(nseChange) }}
                              >
                                ({nseChange > 0 ? "+" : ""}
                                {nseChange.toFixed(2)})
                              </span>
                            </p>
                          </div>
                        </>
                      )}

                      <div className="stat-block">
                        <span>Market Capitalization (₹ Mn.)</span>
                        <p>{formattedMarketCap}</p>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Bottom Right Buttons */}
            <div className={`${displayName}__bottom-right`}>
              <NavLink
                to={`/invits/operational-compare`}
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                <button className="compare">
                  <span className="compare-text">Compare</span>
                  <span className="compare-icon">
                    <img src={compareArrow} alt="compareArrow" />
                  </span>
                </button>
              </NavLink>

              <NavLink
                to={`/invits/${invit._id}/valuation`}
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                <button className="valuation">Valuation</button>
              </NavLink>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InvitBanner;
